// Navigation of the practice console (Practice / Academy / etc.).
import {
  LayoutDashboard, FileText, MessageSquare, CalendarDays, Clock,
  Image as ImageIcon, Quote, Type, ShoppingBag, Sparkles, Tag, Gift,
  Receipt, Settings as SettingsIcon, Users, Share2, Bot,
  Palette, CreditCard, Rocket, GraduationCap, Video, Map, ClipboardList, Globe, Star,
  UserCircle2,
} from "lucide-react";
import { CAP } from "../entitlements/capabilities";

// `to` is the CANONICAL console path ("/admin/..."), never an href: AdminSidebar re-roots
// it onto the current console base through admin/adminPath.js, so the same table serves
// `/admin/*` and `/t/{slug}/admin/*` (SHEET-230).
//
// `capability` names the plan capability the page belongs to. AdminSidebar shows such an
// entry only when the capability is granted AND built (admin/entitlements), and the
// route of the same page is wrapped by RequireCapability with the SAME key in both route
// trees (routes/adminRoutes.jsx, tenant/TenantSite.jsx) — pinned by
// routes/__tests__/adminRouteParity.test.js.
export const NAV = [
  { to: "/admin", labelKey: "nav.overview", icon: LayoutDashboard, end: true, section: "main" },

  // ☀ Practice
  { to: "/admin/calendar", labelKey: "nav.calendar", icon: CalendarDays, section: "practice" },
  { to: "/admin/bookings", labelKey: "nav.bookings", icon: CalendarDays, section: "practice" },
  { to: "/admin/availability", labelKey: "nav.availability", icon: Clock, section: "practice" },
  { to: "/admin/booking-services", labelKey: "nav.bookingServices", icon: Tag, section: "practice" },
  // crm-module BEGIN C1.01 — the old "/admin/customers" entry is gone: the CRM's Clients
  // list replaced that page, and its URL redirects there (both route trees).
  // crm-module END
  // Never capability-gated: a downgraded practice still has paid consultations to run
  // and complete here. Only creating new video business is withheld (inside the page).
  { to: "/admin/telehealth", labelKey: "nav.telehealth", icon: Video, section: "practice" },
  { to: "/admin/modality-packs", labelKey: "nav.modalityPacks", icon: ClipboardList, section: "practice" },
  { to: "/admin/products", labelKey: "nav.products", icon: ShoppingBag, section: "practice" },
  { to: "/admin/programs", labelKey: "nav.programs", icon: Sparkles, section: "practice" },
  // crm-module BEGIN C1.01
  // The CRM section (spec "Admin navigation with the new CRM module"): one entry with six
  // sub-items, open while the section is mounted. `capability` is written BEFORE
  // `children` on purpose: routes/__tests__/adminRouteParity.test.js reads this file as
  // text with `[^}]*capability:` — the first `}` it meets must come after the key.
  // The sub-items are open while the address is inside the section. A child's own
  // `capability` gates that child; `ownerOnly` hides it from a helper
  // (roles.js — the Email Automation screen is owner-only). Every child path must exist
  // in admin/crm/crmRoutes.jsx (pinned by the same test).
  {
    to: "/admin/crm", labelKey: "nav.crm", icon: Users, section: "practice", capability: CAP.crm,
    children: [
      { to: "/admin/crm", labelKey: "nav.crmDashboard", end: true, testId: "admin-nav-home-crm-dashboard" },
      { to: "/admin/crm/clients", labelKey: "nav.crmClients" },
      { to: "/admin/crm/forms", labelKey: "nav.crmForms" },
      { to: "/admin/crm/import", labelKey: "nav.crmImport" },
      { to: "/admin/crm/email", labelKey: "nav.crmEmail", capability: CAP.automation, ownerOnly: true },
      { to: "/admin/crm/packs", labelKey: "nav.crmPacks" },
      // crm-module BEGIN C3.02 (lane F5) — Team (invite/list/resend/remove) is owner-only.
      { to: "/admin/crm/team", labelKey: "nav.crmTeam", ownerOnly: true },
      // crm-module END
      // crm-module BEGIN CR-F3-1 — Settings (the CRM's rules) is owner-only.
      { to: "/admin/crm/settings", labelKey: "nav.crmSettings", ownerOnly: true },
      // crm-module END
    ],
  },
  // crm-module END
  { to: "/admin/promos", labelKey: "nav.promos", icon: Tag, section: "practice" },
  { to: "/admin/giftcards", labelKey: "nav.giftcards", icon: Gift, section: "practice" },
  { to: "/admin/invoices", labelKey: "nav.invoices", icon: Receipt, section: "practice" },
  { to: "/admin/payments", labelKey: "nav.payments", icon: CreditCard, section: "practice" },
  { to: "/admin/tax-config", labelKey: "nav.taxConfig", icon: Receipt, section: "practice" },

  // ☾ Academy — never capability-gated: authoring is withheld inside LearnAdmin, while
  // grading, analytics and pathways serve students a downgraded practice still has.
  { to: "/admin/learn", labelKey: "nav.learn", icon: GraduationCap, section: "academy" },
  { to: "/admin/pathways", labelKey: "nav.pathways", icon: Map, section: "academy" },
  { to: "/admin/learn-analytics", labelKey: "nav.learnAnalytics", icon: GraduationCap, section: "academy" },
  { to: "/admin/quiz-reviews", labelKey: "nav.quizReviews", icon: GraduationCap, section: "academy" },

  // ✦ Intelligence
  { to: "/admin/ai", labelKey: "nav.ai", icon: Bot, section: "intelligence", capability: CAP.aiAssistant },
  // crm-module BEGIN C2.05 — /admin/automation entry retired: Email Automation is a CRM
  // nav child ("nav.crmEmail" above), not a top-level intelligence entry (decision 8).
  // crm-module END
  { to: "/admin/social", labelKey: "nav.social", icon: Share2, section: "intelligence", capability: CAP.social },
  { to: "/admin/affiliate", labelKey: "nav.affiliate", icon: Gift, section: "grow" },

  // Site & Content
  { to: "/admin/site-builder", labelKey: "nav.siteBuilder", icon: Rocket, section: "site" },
  // Never capability-gated: a downgraded practice still sees its domain here; only
  // setting / verifying one is withheld inside the page.
  { to: "/admin/custom-domain", labelKey: "nav.customDomain", icon: Globe, section: "site" },
  { to: "/admin/branding", labelKey: "nav.branding", icon: Palette, section: "site" },
  // Everything the marketplace's "Get to know" block says about the practice in its own
  // words — beside Branding, because it is the other half of how the practice presents.
  { to: "/admin/profile", labelKey: "nav.publicProfile", icon: UserCircle2, section: "site" },
  { to: "/admin/content", labelKey: "nav.content", icon: Type, section: "site" },
  { to: "/admin/media", labelKey: "nav.media", icon: ImageIcon, section: "site" },
  { to: "/admin/site-images", labelKey: "nav.siteImages", icon: ImageIcon, section: "site" },
  { to: "/admin/blog", labelKey: "nav.blog", icon: FileText, section: "site" },
  { to: "/admin/testimonials", labelKey: "nav.testimonials", icon: Quote, section: "site" },
  // Client reviews sit beside testimonials deliberately: both are what people say about the
  // practice on a public page. Testimonials are the practitioner's own selection; reviews
  // are not selectable at all, which is exactly why they need their own screen.
  { to: "/admin/reviews", labelKey: "nav.reviews", icon: Star, section: "site" },
  { to: "/admin/messages", labelKey: "nav.messages", icon: MessageSquare, section: "site" },

  // Settings
  { to: "/admin/billing", labelKey: "nav.billing", icon: CreditCard, section: "settings" },
  { to: "/admin/settings", labelKey: "nav.settings", icon: SettingsIcon, section: "settings" },

  // Roadmap
  { to: "/admin/coming-soon", labelKey: "nav.comingSoon", icon: Rocket, section: "roadmap" },
];

// Section order + the i18n key of each heading (main has no heading).
export const SECTION_ORDER = [
  { key: "main", labelKey: null },
  { key: "practice", labelKey: "section.practice" },
  { key: "academy", labelKey: "section.academy" },
  { key: "intelligence", labelKey: "section.intelligence" },
  { key: "grow", labelKey: "section.grow" },
  { key: "site", labelKey: "section.site" },
  { key: "settings", labelKey: "section.config" },
  { key: "roadmap", labelKey: "section.roadmap" },
];

// crm-module BEGIN C1.01
/**
 * The `data-testid` of a nav entry: its own `testId` when it declares one, otherwise
 * derived from the canonical path — "/admin" → "admin-nav-home",
 * "/admin/crm/clients" → "admin-nav-home-crm-clients". Single-segment paths give exactly
 * the ids the console always had ("admin-nav-home-billing").
 */
export const navTestId = (n) => n.testId ?? `admin-nav-${n.to.replace("/admin", "home").replaceAll("/", "-")}`;
// crm-module END
